import { computed, useId } from 'vue';

type StatusAriaStateType = {
  status: 'default' | 'danger' | 'success';
  isDisabled: boolean;
  hasMessage?: boolean;
  ariaDescribedby?: unknown;
};

/**
 * `aria-invalid` and `aria-describedby` for a form control whose status
 * message is rendered next to it, so the message is announced with the control
 * instead of being visible text only. Merges the caller's own
 * `aria-describedby`. `messageId` goes on the visible message element.
 *
 * @see https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA21 (2026-10-05)
 */
export function useStatusAria(getState: () => StatusAriaStateType) {
  const messageId = useId();
  const ariaAttrs = computed(() => {
    const {
      status,
      isDisabled,
      hasMessage = true,
      ariaDescribedby,
    } = getState();
    const isShowingMessage = status !== 'default' && !isDisabled && hasMessage;
    const describedby = [
      typeof ariaDescribedby === 'string' ? ariaDescribedby : null,
      isShowingMessage ? messageId : null,
    ]
      .filter(Boolean)
      .join(' ');
    return {
      'aria-invalid': status === 'danger' && !isDisabled ? 'true' : undefined,
      'aria-describedby': describedby || undefined,
    };
  });
  return { messageId, ariaAttrs };
}
